import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioEvents, ProStudioContext } from '../prostudio.xstate.types';
import { loadBotPypes } from './load-bot-pypes';
import { Pype } from '../prostudio.api-service.types';
import {
  ChannelMutationProps,
  getChannelsFromPypes,
  mutateChannel,
  resolveBotSolutionId,
} from '../../utils';
import { channelUpdateNeedsCacheInvalidation } from '../channel-update-needs-cache-invalidation';

const findStreamStartChatId = (
  pypes: ProStudioContext['botPypes'],
  streamId: string | undefined
): string | undefined => {
  if (!streamId) return undefined;
  for (const pype of pypes ?? []) {
    const stream = pype?.streams?.find(
      (candidate) => candidate?.id === streamId
    );
    if (stream?.start_chat_bot_id) return stream.start_chat_bot_id;
  }
  return undefined;
};

export const updateChannel = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  botPypes: Pype[];
  channelId: string;
  channelName: string;
  pypeId: string;
  pypeName: string;
  /**
   * Whether the project-wide derived caches must be dropped (PSTUDIO-4253).
   * Computed HERE, before the mutation, because `ctx.botPypes` still holds the
   * pre-update channel to compare against; by the time the machine assigns the
   * result it has been replaced.
   */
  needsCacheInvalidation: boolean;
} | void> => {
  if (event.type !== 'updateChannel') {
    return;
  }

  const { cb, errorCb, ...channelProps } = event.payload;

  // Declared without a value on purpose. It is computed inside the try below —
  // `getChannelsFromPypes` decodes persisted widget config and can throw on a
  // malformed record, and that throw has to reach the `errorCb` path like any
  // other failure, or the caller sits on "Saving…" until its 45s backstop.
  // `undefined` is therefore a real state here, and the return resolves it to
  // the safe answer rather than seeding one that is always overwritten.
  let needsCacheInvalidation: boolean | undefined;

  // Any failure — context validation OR the mutation — must settle the caller's
  // promise via errorCb before rethrowing, otherwise "Save and leave" hangs
  // forever on "Saving…". Rethrow still routes the machine to botFlowError.
  try {
    const projectId = ctx.project?.id;
    const uniqueCustomerId = ctx.project?.projectProStudioSetting?.customerId;
    // Written verbatim as the voice stream's `startBot`, so it must be the id the
    // stream already carries: `<customerName>.<uuid>` for a channel created since the
    // migration, the legacy `botSolutionId` for an older one (the backend never
    // rewrote those). Rebuilding it re-pointed the channel at a bot id no pype read
    // matches; the uuid form is only the fallback for a stream not in the list.
    const botId =
      findStreamStartChatId(ctx.botPypes, event.payload.id) ??
      resolveBotSolutionId(ctx);

    if (!projectId) {
      throwErrorWithId('Project ID is required');
      return;
    }

    if (!uniqueCustomerId) {
      throwErrorWithId('Customer ID is required');
      return;
    }

    if (!botId) {
      throwErrorWithId('Bot ID is required');
      return;
    }

    const channel: ChannelMutationProps = channelProps;

    // Before the mutation: `ctx.botPypes` still holds the pre-update channel to
    // compare against, and the machine replaces it with this invoke's result.
    needsCacheInvalidation = channelUpdateNeedsCacheInvalidation({
      channels: getChannelsFromPypes(ctx.botPypes ?? []),
      updated: {
        id: event.payload.id,
        name: event.payload.name,
        pypeId: event.payload.pypeId,
        env: event.payload.env,
      },
    });

    await mutateChannel({
      channel,
      projectId,
      requestAction: 'update',
      cb,
      uniqueCustomerId,
      botId,
    });
  } catch (error) {
    errorCb?.();
    throw error;
  }

  const { botPypes } = await loadBotPypes(ctx, event);

  // The v3 config API and pypes v6 API don't sync immediately, so stream.name
  // may still reflect the old value. Patch optimistically to keep the UI consistent.
  const updatedChannelId = event.payload.id;
  const updatedChannelName = event.payload.name;
  const patchedBotPypes =
    updatedChannelId && updatedChannelName
      ? botPypes.map((pype) => ({
          ...pype,
          streams: pype.streams?.map((stream) =>
            stream?.id === updatedChannelId
              ? { ...stream, name: updatedChannelName }
              : stream
          ),
        }))
      : botPypes;

  return {
    botPypes: patchedBotPypes,
    channelId: event.payload.id,
    channelName: event.payload.name || '',
    pypeId: event.payload.pypeId,
    pypeName: botPypes[0]?.name || '',
    // Under-invalidating is the expensive direction — it brings back the stale
    // channel lists this flag exists to prevent — so an unreached computation
    // resolves to "invalidate".
    needsCacheInvalidation: needsCacheInvalidation ?? true,
  };
};
